import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AdminCompanyContext } from '../../AdminApp';
import { getAdminCompanies } from '../../adminApi';

const COLUMNS = [
  { key: 'label',              label: 'Company',     fmt: 'label' },
  { key: 'company_id',         label: 'ID',          fmt: 'id'    },
  { key: 'user_count',         label: 'Users',       fmt: 'int' },
  { key: 'query_count',        label: 'Queries',     fmt: 'int' },
  { key: 'total_tokens',       label: 'Tokens',      fmt: 'int' },
  { key: 'total_cost_usd',     label: 'Cost',        fmt: 'usd' },
  { key: 'last_active',        label: 'Last Active', fmt: 'date' },
  { key: 'feedback_count',     label: 'Feedback',    fmt: 'int' },
  { key: 'avg_score',          label: 'Avg Score',   fmt: 'dec2' },
  { key: 'success_rate',       label: 'Success %',   fmt: 'pct' },
];

function fmtVal(value, type) {
  if (value === null || value === undefined) return '—';
  switch (type) {
    case 'int':  return Number(value).toLocaleString();
    case 'usd':  return `$${Number(value).toFixed(2)}`;
    case 'pct':  return `${(value * 100).toFixed(1)}%`;
    case 'dec2': return Number(value).toFixed(2);
    case 'date': {
      const d = new Date(value);
      return isNaN(d) ? value : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    }
    case 'id':
      return value.length > 18 ? value.slice(0, 8) + '…' + value.slice(-4) : value;
    case 'label': return value || '—';
    default: return String(value);
  }
}

function activityBadge(lastActive) {
  if (!lastActive) return null;
  const days = (Date.now() - new Date(lastActive).getTime()) / 86_400_000;
  if (days < 7)  return <span className="admin-badge-active">Active</span>;
  if (days > 30) return <span className="admin-badge-inactive">Inactive</span>;
  return null;
}

export default function AdminCompanies() {
  const { selectedCompany, setSelectedCompany, setCompanyLabel } = useContext(AdminCompanyContext);
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [sortKey, setSortKey]   = useState('query_count');
  const [sortDir, setSortDir]   = useState('desc');

  const load = useCallback(async () => {
    try {
      const data = await getAdminCompanies();
      setRows(data);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  }

  const sorted = [...rows].sort((a, b) => {
    const av = a[sortKey] ?? '';
    const bv = b[sortKey] ?? '';
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div>
      <div className="admin-companies-header">
        <h2 className="admin-section-title" style={{ margin: 0 }}>Companies</h2>
        {selectedCompany && (
          <div className="admin-active-filter">
            <span>Filtered: <strong title={selectedCompany}>{selectedCompany.slice(0, 10)}…</strong></span>
            <button onClick={() => { setSelectedCompany(null); setCompanyLabel(null); }}>Clear filter</button>
          </div>
        )}
      </div>

      {loading && (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>{COLUMNS.map(c => <th key={c.key}>{c.label}</th>)}</tr>
            </thead>
            <tbody>
              {[1, 2, 3].map(i => (
                <tr key={i}>
                  {COLUMNS.map(c => (
                    <td key={c.key}><div className="admin-skeleton" style={{ height: 14, width: '70%' }} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {error && (
        <div className="admin-error-state">
          <p>{error}</p>
          <button onClick={() => { setLoading(true); load(); }}>Retry</button>
        </div>
      )}

      {!loading && !error && (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                {COLUMNS.map(({ key, label }) => (
                  <th
                    key={key}
                    className="admin-th-sortable"
                    onClick={() => handleSort(key)}
                  >
                    {label}
                    {sortKey === key && (
                      <span className="admin-sort-arrow">{sortDir === 'asc' ? ' ↑' : ' ↓'}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map(row => (
                <tr
                  key={row.company_id}
                  onClick={() => { setSelectedCompany(row.company_id); setCompanyLabel(row.label || row.company_id); }}
                  className={selectedCompany === row.company_id ? 'admin-row-selected' : ''}
                  title="Click to filter by this company"
                >
                  {COLUMNS.map(({ key, fmt }) => (
                    <td key={key}>
                      {key === 'last_active'
                        ? <>{fmtVal(row[key], fmt)} {activityBadge(row[key])}</>
                        : fmtVal(row[key], fmt)
                      }
                    </td>
                  ))}
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr><td colSpan={COLUMNS.length} style={{ textAlign: 'center', color: 'var(--admin-text-muted)' }}>No companies found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
